import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import videoService from '../../services/videoService';

interface VideoPreviewModalProps {
  isOpen: boolean;
  title: string;
  youtubeUrl: string;
  onConfirm: () => void;
  onCancel: () => void;
  showActions?: boolean;
  isUploading?: boolean;
}

const VideoPreviewModal: React.FC<VideoPreviewModalProps> = ({
  isOpen,
  title,
  youtubeUrl,
  onConfirm,
  onCancel,
  showActions = true,
  isUploading = false,
}) => {
  const embedUrl = useMemo(() => {
    if (!youtubeUrl) return '';
    return videoService.getYouTubeEmbedUrl(youtubeUrl);
  }, [youtubeUrl]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-apple shadow-apple-lg w-full max-w-3xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{title || 'Video'}</h3>
            <p className="text-xs text-gray-500 truncate">{youtubeUrl}</p>
          </div>
          <button onClick={onCancel} className="p-2 rounded-apple hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4">
          {embedUrl ? (
            <div className="w-full aspect-video bg-black rounded-apple overflow-hidden">
              <iframe
                title={title || 'Video'}
                src={embedUrl}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="p-6 text-sm text-gray-600">No YouTube URL provided.</div>
          )}
        </div>

        {showActions && (
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isUploading}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={isUploading}>
              {isUploading ? 'Uploading...' : 'Confirm'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPreviewModal;

